import { formatDate, toDate } from "@/utils/dateUtil"
import { replaceHtmlTags } from "@/utils/helpers"
import { syncDescriptionHtml } from "@/utils/syncDescriptionHtml"

import { Base } from "./Base"
import { StructuredDescription } from "./EditorData"
import { FileMetaData } from "./File"
import { ReferencedResourcePreviews } from "./Search"
import { Team } from "./Team"
import { User } from "./User"

import type { ItemPreviewDto } from "./Item"

export type ToDoType = "OPENED" | "SENT" | "CLOSED"
export type ToDoItemType =
  | "REGULAR"
  | "COMPLETE_RETROSPECTIVE_REQUEST"
  | "STORY_POKER_REQUEST"
  | "SUBMIT_TIMESHEET_REQUEST"
  | "TASK_BREAKDOWN"
  | "PM_REVIEW_REQUEST"
  | "CLIENT_REVIEW_REQUEST"
  | "STATUS_CHANGE_COMMENT_LOW_SCORE"
  | ""

export interface ToDoCount {
  count: number
}

export class ToDoMetrics {
  myTodos = 0
  sentToOthers = 0
  urgent = 0
  today = 0
  pastDue = 0

  constructor(props?: Partial<ToDoMetrics>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface ToDoRelatedResource {
  id: string
  type?: string
}

export type ToDoByType = {
  [key in ToDoType]: ToDo[]
}

export type ToDoStatus = "ACTIVE" | "COMPLETED" | "OBSOLETE"
export type ToDoCompletionRule = "ANYONE" | "EVERYONE"

export interface OwnersCompletion {
  [key: string]: boolean
}
export class ToDo extends Base<ToDo> {
  completedOn = new Date()
  createdAutomatically = false
  createdDate = new Date()
  estimatedDeadline?: Date
  id = ""
  sequenceNumber = 0
  label = ""
  name = ""
  owner = new User()
  owners: User[] = []
  ownersCompletionStatuses: OwnersCompletion = {}
  projectId = ""
  relatedResource: ToDoRelatedResource = {
    id: "",
    type: ""
  }
  relatedUrl = ""
  status: ToDoStatus = "ACTIVE"
  structuredDescription = new StructuredDescription()
  referencedResourcePreviews?: ReferencedResourcePreviews
  urgent = false
  removePrevious = false
  addMode = false
  selectedTeams: Team[] = []
  disableSelectionTeam = false
  isStoryPoker = false
  /** Linked Item returned for an Item-related ToDo. */
  item?: ItemPreviewDto
  itemId = ""
  type: ToDoItemType = ""
  completedByMe = false
  completionRule: ToDoCompletionRule = "ANYONE"
  createdBy: User = new User()
  filterByTeamId = ""
  fileMetadata: FileMetaData[] = []
  fileMetadataIds: string[] = []
  constructor(props?: Partial<ToDo>) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      if (field === "completedOn" || field === "createdDate") {
        this[field] = toDate(props[field] ?? "")
        return
      }
      if (field === "owners") {
        const users = props[field] ?? []
        this[field] = users.map(owner => new User(owner))
        return
      }
      this[field] = props[field]
    })
    this.label = props.label ?? ""
    this.structuredDescription = new StructuredDescription(props.structuredDescription)
    this.structuredDescription.html = syncDescriptionHtml({
      referencedResourcePreviews: props.referencedResourcePreviews,
      structuredDescription: this.structuredDescription
    })
    this.createdBy = new User(this.createdBy)
    this.fileMetadata = (this.fileMetadata ?? []).map(file => FileMetaData.create(file))
    this.fileMetadataIds = props.fileMetadataIds ?? this.fileMetadata.map(file => file.id)
  }

  getJsonObj() {
    return {
      ...this.getUpdateJsonObj(),
      type: this.type || undefined,
      relatedUrl: this.relatedUrl || undefined,
      removePrevious: this.removePrevious,
      fileMetadataIds: this.fileMetadataIds
    }
  }

  getUpdateJsonObj() {
    const isTaskBreakdown = this.type === "TASK_BREAKDOWN"
    const relatedResource = isTaskBreakdown
      ? {
          ...this.relatedResource,
          type: "ITEM"
        }
      : this.relatedResource

    return {
      name: this.getToDoName(),
      ownerIds: this.owners.map(owner => owner.id),
      projectId: this.projectId,
      relatedResource,
      structuredDescription: new StructuredDescription(this.structuredDescription).getJsonObj(),
      label: this.label || null,
      completionRule: this.completionRule,
      urgent: this.urgent,
      estimatedDeadline: this.estimatedDeadline
        ? formatDate(this.estimatedDeadline, "yyyy-MM-dd", { dateOnly: true })
        : null
    }
  }

  getToDoName() {
    let name = this.name
    if (name.length > 128) {
      name = `${this.name.slice(0, 125)}...`
    }
    return name
  }

  get changeByFields() {
    return [
      "estimatedDeadline",
      "label",
      "structuredDescription",
      "completionRule",
      "urgent",
      "owners"
    ] as (keyof ToDo)[]
  }

  isModified(keys?: (keyof ToDo)[], inverse?: boolean) {
    let isModified = false
    if (keys?.includes("structuredDescription") && this.initialState) {
      const initial = JSON.parse(this.initialState)
      if (initial.structuredDescription) {
        isModified =
          replaceHtmlTags(this.structuredDescription?.html) !==
          replaceHtmlTags(initial.structuredDescription?.html)
      }
      keys = keys.filter(key => key !== "structuredDescription")
    }
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }

  get isDone() {
    return this.status === "COMPLETED"
  }
}
